/**
 * Integrytas Group - Base App (MOBILE)
 * Page: ADI.WATCHDOG — mobile-native (READ-ONLY).
 *
 * Touch-first counterpart of src/pages/admin/AdiAssertionsPage.tsx. A DISTINCT
 * presentation (KPI summary + dense card list + bottom-sheet detail with a
 * recent-results timeline) over the SAME data layer: reads via apiDb
 * (adi_assertion, adi_result, and une_workspace for the FK name), computing each
 * assertion's LATEST pass/fail result for a scannable health signal. It is
 * read-only — no writes — so it never touches the Common Routine (§2). §16:
 * never the desktop grid re-hosted in a phone viewport.
 *
 * Enum labels (category / severity / result status) are CHECK-enums, rendered via
 * i18n labelled constants (§17.B11), never the bare stored literal. The workspace
 * FK is resolved to its name (§17.B4); the Y/N active flag renders as a status
 * pill (§17.B10). Dates go through useFormatDateTime (§11).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Shield, ShieldCheck, ShieldAlert } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobileLeadTone, type MobilePillTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// How many recent result rows to scan for each assertion's latest status.
const RESULT_WINDOW = 500;
// How many recent results to show in a selected assertion's detail timeline.
const DETAIL_RESULT_LIMIT = 20;

// CHECK-enum labelled constants (§17.B11) — stored value stays the code; only the
// displayed label is localized. Category/severity/status keys reuse the desktop's
// existing translation keys where they exist (adi.severity.*, adi.status.*).
const CATEGORY_META: Record<string, { labelKey: string; label: string }> = {
  STRUCTURAL: { labelKey: 'mobile.adi.category.structural', label: 'Structural' },
  LOGICAL: { labelKey: 'mobile.adi.category.logical', label: 'Logical' },
  ORPHAN: { labelKey: 'mobile.adi.category.orphan', label: 'Orphan' },
  PARAMETER: { labelKey: 'mobile.adi.category.parameter', label: 'Parameter' },
};

const SEVERITY_META: Record<string, { labelKey: string; label: string; pill: string }> = {
  CRITICAL: { labelKey: 'adi.severity.critical', label: 'Critical', pill: 'm-p-no' },
  HIGH: { labelKey: 'adi.severity.high', label: 'High', pill: 'm-p-wait' },
  MEDIUM: { labelKey: 'adi.severity.medium', label: 'Medium', pill: 'm-p-info' },
  LOW: { labelKey: 'adi.severity.low', label: 'Low', pill: 'm-p-info' },
};

// Latest-result status → pill + card lead accent. NONE = assertion never run.
const RESULT_META: Record<string, { labelKey: string; label: string; pill: string; lead: string }> = {
  PASS: { labelKey: 'adi.status.pass', label: 'Pass', pill: 'm-p-ok', lead: 'm-l-green' },
  FAIL: { labelKey: 'adi.status.fail', label: 'Fail', pill: 'm-p-no', lead: 'm-l-red' },
  ERROR: { labelKey: 'adi.status.error', label: 'Error', pill: 'm-p-wait', lead: 'm-l-amber' },
  NONE: { labelKey: 'mobile.adi.status.none', label: 'Not run', pill: 'm-p-info', lead: 'm-l-slate' },
};

// Independent is_active facet (§17.B12) — defaults to Active-only on first load.
type ActiveFacet = 'all' | 'active' | 'inactive';

interface LatestResult {
  status: string;
  created_at: string;
  offending_count: number | null;
}

interface AssertRow {
  adi_assertion_id: number;
  internal_code: string;
  name: string | null;
  category: string;
  severity: string;
  view_name: string | null;
  une_workspace_id: number | null;
  is_active: 'Y' | 'N';
  description: string | null;
  workspace_name: string | null;
  last_status: string | null;
  last_run_at: string | null;
  last_offending: number | null;
}

interface ResultTimelineRow {
  adi_result_id: number;
  status: string;
  offending_count: number | null;
  error_message: string | null;
  created_at: string;
}

// Raw shapes straight off the gateway (before enrichment).
interface RawAssert {
  adi_assertion_id: number;
  internal_code: string;
  name: string | null;
  category: string;
  severity: string;
  view_name: string | null;
  une_workspace_id: number | null;
  is_active: 'Y' | 'N';
  description: string | null;
}
interface RawResult {
  adi_assertion_id: number | null;
  status: string;
  offending_count: number | null;
  created_at: string;
}
interface WsLite {
  une_workspace_id: number;
  name: string;
}

function resultKey(r: AssertRow): string {
  return r.last_status ?? 'NONE';
}

function leadIcon(statusKey: string) {
  if (statusKey === 'PASS') return <ShieldCheck />;
  if (statusKey === 'FAIL' || statusKey === 'ERROR') return <ShieldAlert />;
  return <Shield />;
}

function leadTone(statusKey: string): MobileLeadTone {
  if (statusKey === 'PASS') return 'green';
  if (statusKey === 'FAIL') return 'red';
  if (statusKey === 'ERROR') return 'amber';
  return 'slate';
}

// Map a legacy m-p-* pill class to the MobileStatusPill tone.
const PILL_TONE_BY_CLASS: Record<string, MobilePillTone> = {
  'm-p-ok': 'ok', 'm-p-no': 'no', 'm-p-wait': 'wait', 'm-p-info': 'info', 'm-p-you': 'you',
};
function pillTone(cls: string | undefined): MobilePillTone {
  return (cls && PILL_TONE_BY_CLASS[cls]) || 'info';
}

export function MobileAdiAssertionsPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const fmtDateTime = useFormatDateTime();

  const [rows, setRows] = useState<AssertRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<string>('all');
  const [activeFacet, setActiveFacet] = useState<ActiveFacet>('active');
  const [selected, setSelected] = useState<AssertRow | null>(null);
  const [timeline, setTimeline] = useState<ResultTimelineRow[]>([]);
  const [timelineBusy, setTimelineBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [a, r, w] = await Promise.all([
        apiDb
          .from('adi_assertion')
          .select('adi_assertion_id, internal_code, name, category, severity, view_name, une_workspace_id, is_active, description')
          .order('severity')
          .order('internal_code'),
        apiDb
          .from('adi_result')
          .select('adi_assertion_id, status, offending_count, created_at')
          .order('created_at', { ascending: false })
          .limit(RESULT_WINDOW),
        // §17.B4: resolve the workspace FK to its name (une_workspace.name is a
        // plain column — no _tl to join).
        apiDb.from('une_workspace').select('une_workspace_id, name'),
      ]);
      if (a.error) throw a.error;
      if (r.error) throw r.error;
      if (w.error) throw w.error;

      // Latest result per assertion — results arrive newest-first, so the first
      // occurrence of an assertion id within the window is its most recent run.
      const latest: Record<number, LatestResult> = {};
      for (const res of (r.data ?? []) as RawResult[]) {
        if (res.adi_assertion_id == null) continue;
        if (latest[res.adi_assertion_id]) continue;
        latest[res.adi_assertion_id] = {
          status: res.status,
          created_at: res.created_at,
          offending_count: res.offending_count,
        };
      }

      const wsNames: Record<number, string> = {};
      for (const ws of (w.data ?? []) as WsLite[]) wsNames[ws.une_workspace_id] = ws.name;

      const enriched: AssertRow[] = ((a.data ?? []) as RawAssert[]).map((row) => {
        const lr = latest[row.adi_assertion_id];
        return {
          ...row,
          workspace_name: row.une_workspace_id == null ? null : (wsNames[row.une_workspace_id] ?? null),
          last_status: lr?.status ?? null,
          last_run_at: lr?.created_at ?? null,
          last_offending: lr?.offending_count ?? null,
        };
      });
      setRows(enriched);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  // Load the recent-results timeline for the selected assertion.
  useEffect(() => {
    if (!selected) { setTimeline([]); return; }
    let cancelled = false;
    setTimelineBusy(true);
    void apiDb
      .from('adi_result')
      .select('adi_result_id, status, offending_count, error_message, created_at')
      .eq('adi_assertion_id', selected.adi_assertion_id)
      .order('created_at', { ascending: false })
      .limit(DETAIL_RESULT_LIMIT)
      .then(({ data }) => {
        if (cancelled) return;
        setTimeline((data ?? []) as ResultTimelineRow[]);
        setTimelineBusy(false);
      });
    return () => { cancelled = true; };
  }, [selected]);

  const categoryLabel = (code: string) =>
    CATEGORY_META[code] ? t(CATEGORY_META[code].labelKey, CATEGORY_META[code].label) : code;
  const severityLabel = (code: string) =>
    SEVERITY_META[code] ? t(SEVERITY_META[code].labelKey, SEVERITY_META[code].label) : code;
  const resultLabel = (key: string) =>
    RESULT_META[key] ? t(RESULT_META[key].labelKey, RESULT_META[key].label) : key;

  // §17.B5: latest-result facet chips, limited to the statuses actually present.
  const statusOptions = useMemo(() => {
    const present = new Set(rows.map(resultKey));
    return (['PASS', 'FAIL', 'ERROR', 'NONE'] as const)
      .filter((k) => present.has(k))
      .map((k) => ({ key: k, label: resultLabel(k) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (activeFacet === 'active' && r.is_active !== 'Y') return false;
      if (activeFacet === 'inactive' && r.is_active !== 'N') return false;
      if (statusFacet !== 'all' && resultKey(r) !== statusFacet) return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        categoryLabel(r.category).toLowerCase().includes(q) ||
        severityLabel(r.severity).toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, statusFacet, activeFacet]);

  const kpis = useMemo(() => {
    let active = 0;
    let failing = 0;
    for (const r of rows) {
      if (r.is_active === 'Y') active += 1;
      if (r.last_status === 'FAIL' || r.last_status === 'ERROR') failing += 1;
    }
    return { total: rows.length, active, failing };
  }, [rows]);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.adi.title', 'Data Integrity')}
        description={t('mobile.adi.description', 'Watchdog assertions with their latest pass or fail result.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.total, label: t('mobile.adi.kpi_total', 'Assertions') },
          { value: kpis.active, label: t('mobile.adi.kpi_active', 'Active') },
          { value: kpis.failing, label: t('mobile.adi.kpi_failing', 'Failing') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.adi.search', 'Search assertions')}
          aria-label={t('mobile.adi.search', 'Search assertions')}
        />
      </div>

      <MobileFilterChips
        chips={[{ value: 'all', label: t('mobile.adi.all', 'All') }, ...statusOptions.map((o) => ({ value: o.key, label: o.label }))]}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v)}
      />

      {/* Independent is_active facet (§17.B12), defaulting to Active-only. */}
      <MobileFilterChips
        chips={[
          { value: 'all', label: t('mobile.adi.all', 'All') },
          { value: 'active', label: t('common.boolean.active', 'Active') },
          { value: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
        ]}
        isActive={(v) => activeFacet === v}
        onSelect={(v) => setActiveFacet(v as ActiveFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.adi.none', 'No assertions match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => {
            const key = resultKey(r);
            return (
              <MobileCardRow
                key={r.adi_assertion_id}
                lead={leadIcon(key)}
                leadTone={leadTone(key)}
                title={r.name || r.internal_code}
                subtitle={`${r.internal_code} · ${severityLabel(r.severity)}`}
                trailing={
                  <>
                    <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                      {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                    </MobileStatusPill>
                    <MobileStatusPill tone={pillTone(RESULT_META[key]?.pill)}>
                      {resultLabel(key)}
                    </MobileStatusPill>
                  </>
                }
                chevron
                onClick={() => setSelected(r)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.adi.detail', 'Assertion detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{leadIcon(resultKey(selected))}</span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.adi.status_lbl', 'Latest result')}
                value={<MobileStatusPill tone={pillTone(RESULT_META[resultKey(selected)]?.pill)}>{resultLabel(resultKey(selected))}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.adi.severity', 'Severity')}
                value={<MobileStatusPill tone={pillTone(SEVERITY_META[selected.severity]?.pill)}>{severityLabel(selected.severity)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.adi.category_lbl', 'Category')} value={categoryLabel(selected.category)} />
              <MobileDetailRow
                label={t('mobile.adi.active', 'Active')}
                value={<MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>{selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.adi.workspace', 'Workspace')} value={selected.workspace_name ?? t('mobile.adi.all_workspaces', 'All workspaces')} />
              <MobileDetailRow label={t('mobile.adi.source', 'Source')} value={selected.view_name ? `${t('mobile.adi.view', 'View')}: ${selected.view_name}` : t('mobile.adi.check_sql', 'Check SQL')} />
              <MobileDetailRow label={t('mobile.adi.last_run', 'Last run')} value={selected.last_run_at ? fmtDateTime(selected.last_run_at) : t('mobile.adi.never', 'Never')} />
              <MobileDetailRow label={t('mobile.adi.offending', 'Offending rows')} value={selected.last_offending ?? '—'} />
              {selected.description && (
                <MobileDetailRow label={t('mobile.adi.description', 'Description')} value={selected.description} />
              )}
            </MobileContentCard>

            {/* Recent-results timeline (adi_result) */}
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.adi.history', 'History')}
                value={timelineBusy ? t('common.loading', 'Loading…') : `${timeline.length}`}
              />
              {!timelineBusy && timeline.length === 0 && (
                <div className="m-empty">{t('mobile.adi.no_results', 'No results yet — run a scan.')}</div>
              )}
              {timeline.map((ev) => (
                <MobileDetailRow
                  key={ev.adi_result_id}
                  label={fmtDateTime(ev.created_at)}
                  value={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MobileStatusPill tone={pillTone(RESULT_META[ev.status]?.pill)}>{resultLabel(ev.status)}</MobileStatusPill>
                      {ev.offending_count != null ? ev.offending_count : ''}
                    </span>
                  }
                />
              ))}
            </MobileContentCard>

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
                {t('mobile.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
